<template>
  <Basic>
    <div class="grid grid--container">
      <div class="row">
        <div class="col col--lg-9 col--centered">
          <h1 class="basic__title">Terms of Service</h1>
          <Rte>
            <p>Last updated: 10/19/2026</p>

            <h2>Terms</h2>

            <p>
              These Terms of Service ("Terms") govern your use of the website,
              web app, and services (together or individually, "Service")
              offered by Abyme Campsite LLC ("Campsite.bio", "we", "our", "us",
              "Company"). "You" refers to you as a user of the Service.
            </p>

            <p>
              By accessing the website or web app, you are agreeing to be bound
              by these terms of service, all applicable laws and regulations,
              and agree that you are responsible for compliance with any
              applicable local laws. If you do not agree with any of these
              terms, you are prohibited from using or accessing this site. The
              materials contained in this website are protected by applicable
              copyright and trademark law.
            </p>

            <h2>Content Policy</h2>

            <p>
              Our Service allows you to post, link, store, share and otherwise
              make available certain information, text, graphics, videos, or
              other material ("Content"). You are responsible for Content that
              you post on or through Service, including its legality,
              reliability, and appropriateness.
            </p>

            <p>
              Our
              <nuxt-link to="/community-standards"
                >Community Standards</nuxt-link
              >
              set out what content is and isn’t allowed on Campsite, so please
              make sure you follow them, otherwise we may suspend or permanently
              remove your account. When we talk about your “content”, we mean
              the text, graphics, videos, links, and any other materials you add
              to your Campsite profile.
            </p>

            <p>
              By posting Content on or through Service, you represent and
              warrant that: (i) Content is yours (you own it) and/or you have
              the right to use it and the right to grant us the rights and
              license as provided in these Terms, and (ii) that the posting of
              your Content on or through Service does not violate the privacy
              rights, publicity rights, copyrights, contract rights, or any
              other rights of any person or entity. We reserve the right to
              terminate the account of anyone found to be infringing on any of
              these rights.
            </p>

            <p>
              You retain any and all of your rights to any Content you submit,
              post, or display on or through Service and you are responsible for
              protecting those rights. We take no responsibility and assume no
              liability for Content you or any third party posts on or through
              Service. However, by posting Content using Service you grant us
              the right and license to use, modify, publicly perform, publicly
              display, reproduce, and distribute such Content on and through
              Service. You agree that this license includes the right for us to
              make your Content available to other users of Service, who may
              also use your Content subject to these Terms.
            </p>

            <p>
              Campsite, has the right but not the obligation to monitor, edit,
              and remove all Content provided by users. It is your
              responsibility to exercise judgment when posting content on the
              Service.
            </p>

            <p>
              In addition, Content found on or through this Service are the
              property of Campsite or used with permission. You may not
              distribute, modify, transmit, reuse, download, repost, copy, or
              use said Content, whether in whole or in part, for commercial
              purposes or for personal gain, without express advance written
              permission from us.
            </p>

            <h2 id="copyright-dmca">Copyright / DMCA</h2>

            <p>
              Campsite.bio respects copyright and responds to notices of claimed
              infringement under the Digital Millennium Copyright Act (DMCA).
              If you own a copyright or are authorized to act for its owner,
              send a written notice to our designated agent by email or mail:
            </p>

            <CopyrightAgent />

            <h3>Submitting a copyright notice</h3>
            <p>Your notice should include substantially the following:</p>
            <ol>
              <li>Your physical or electronic signature.</li>
              <li>
                Identification of the copyrighted work you claim has been
                infringed, or a representative list if your notice covers
                multiple works at a single online site.
              </li>
              <li>
                Identification of the material or link you want removed or
                disabled, with enough information for us to locate it. Please
                include the specific Campsite.bio profile URL and identify the
                content or link on that profile.
              </li>
              <li>
                Your contact information, including your mailing address,
                telephone number, and email address, if available.
              </li>
              <li>
                A statement that you have a good-faith belief that the use you
                are reporting is not authorized by the copyright owner, its
                agent, or the law.
              </li>
              <li>
                A statement that the information in your notice is accurate
                and, under penalty of perjury, that you are authorized to act
                on behalf of the owner of the exclusive right allegedly
                infringed.
              </li>
            </ol>
            <p>
              We act expeditiously to remove or disable access to material or
              links in response to valid notices. When we remove material
              posted by a user, we take reasonable steps to promptly notify
              that user. We may share your notice with the affected user.
            </p>

            <h3>Submitting a counter-notice</h3>
            <p>
              If you believe your material was removed or disabled by mistake
              or misidentification, you may send a written counter-notice to
              the designated agent above. Include:
            </p>
            <ol>
              <li>Your physical or electronic signature.</li>
              <li>
                Identification of the removed or disabled material and its
                location before it was removed or disabled.
              </li>
              <li>
                A statement under penalty of perjury that you have a good-faith
                belief that the material was removed or disabled because of a
                mistake or misidentification.
              </li>
              <li>
                Your name, address, and telephone number, and a statement that
                you consent to the jurisdiction of the Federal District Court
                for the judicial district where your address is located (or,
                if you are outside the United States, any judicial district
                where Campsite.bio may be found), and that you will accept
                service of process from the person who submitted the original
                notice or that person's agent.
              </li>
            </ol>
            <p>
              We will provide a copy of a valid counter-notice to the person
              who submitted the original notice. We will restore the material
              no sooner than 10 and no later than 14 business days after
              receiving the counter-notice, unless our designated agent first
              receives notice that the original complainant has filed a court
              action seeking to restrain the user's infringing activity
              relating to that material.
            </p>
            <p>
              Knowingly making a material misrepresentation in a notice or
              counter-notice may result in liability under the DMCA. Our policy
              is to terminate, in appropriate circumstances, the accounts of
              users who repeatedly infringe copyright.
            </p>

            <h2>Security</h2>
            <p>
              It is Your responsibility to secure your username and password
              from theft or any other means of unauthorized use that would
              violate these Terms and Conditions of Use. We do not store any
              whole credit card numbers or payment information, and instead,
              these are processed through third party processors such as Stripe.
              By utilizing these payment processors to gain access to the
              Offering, You indemnify Us and instead assume any and all risk or
              liability for the security of the payment details, and agree to be
              bound by the third party payment processor’s applicable terms and
              conditions of use.
            </p>

            <h2>Recurring Payments</h2>

            <p>
              If you have signed up for a payment plan, You hereby authorize our
              continued access to your financial information stored by our third
              party financial processing company referenced in this Agreement
              until your payment plan is complete, as set forth in Your
              acceptance of the purchase terms upon checkout.
            </p>

            <h2>Transaction Fees</h2>

            <p>
              We may charge a percentage of all transactions made on your
              profile (for example through our “Request” or “Support” features).
              The transaction fees charged will change based on your plan and is
              available on our
              <nuxt-link to="/pricing">pricing page</nuxt-link>.
            </p>

            <p>
              By using our platform you agree that if we charge transactions
              fees, the fee will be deducted from the proceeds of your
              transaction prior to receipt. You alone are responsible for paying
              any external fees and taxes, including withholding taxes, if they
              apply, and income taxes, associated with payments you receive
              through the Campsite platform.
            </p>

            <h2>Processing Fees</h2>

            <p>
              We use third party payment services, including PayPal, Stripe, and
              Square, to power your transactions on Campsite. Each of these
              services have their own processing fees in accordance with their
              terms and conditions. You are responsible for reviewing,
              accepting, and complying with their terms.
            </p>

            <p>
              You agree that processing fees will be deducted from the proceeds
              of your transaction prior to receipt and that Campsite is not
              responsible for these fees.
            </p>

            <p>
              Campsite's transaction and subscription fees are subject to
              change, but we'll always provide prior notice. We will aim for at
              least a month of notice before changes would take place. If you
              don't agree with the revised fees, you can cancel your plan.
            </p>

            <h2>Refunds</h2>

            <p>
              To cancel, visit the
              <a
                href="https://app.campsite.bio/account/change-plan"
                target="_blank"
                >billing page</a
              >. To the extent permitted by applicable law, payments are
              non-refundable. We know that at times your requirements can
              change. So, if you've upgraded to a paid plan, but cancel within
              72 hours, we may make an exception. Please email us at
              support@campsite.bio.
            </p>

            <h2>Use License</h2>

            <p>
              Permission is granted to temporarily download one copy of the
              materials (information or software) on Campsite's website for
              personal, non-commercial transitory viewing only. This is the
              grant of a license, not a transfer of title, and under this
              license you may not: modify or copy the materials; use the
              materials for any commercial purpose, or for any public display
              (commercial or non-commercial); attempt to decompile or reverse
              engineer any software contained on Campsite's website; remove any
              copyright or other proprietary notations from the materials; or
              transfer the materials to another person or "mirror" the materials
              on any other server. This license shall automatically terminate if
              you violate any of these restrictions and may be terminated by
              Campsite at any time. Upon terminating your viewing of these
              materials or upon the termination of this license, you must
              destroy any downloaded materials in your possession whether in
              electronic or printed format.
            </p>

            <h2>Disclaimer</h2>

            <p>
              The materials on Campsite's website are provided on an 'as is'
              basis. Campsite makes no warranties, expressed or implied, and
              hereby disclaims and negates all other warranties including,
              without limitation, implied warranties or conditions of
              merchantability, fitness for a particular purpose, or
              non-infringement of intellectual property or other violation of
              rights. Further, Campsite does not warrant or make any
              representations concerning the accuracy, likely results, or
              reliability of the use of the materials on its website or
              otherwise relating to such materials or on any sites linked to
              this site.
            </p>

            <h2>Limitations</h2>

            <p>
              In no event shall Campsite or its suppliers be liable for any
              damages (including, without limitation, damages for loss of data
              or profit, or due to business interruption) arising out of the use
              or inability to use the materials on Campsite's website, even if
              Campsite or a Campsite authorized representative has been notified
              orally or in writing of the possibility of such damage. Because
              some jurisdictions do not allow limitations on implied warranties,
              or limitations of liability for consequential or incidental
              damages, these limitations may not apply to you.
            </p>

            <h2>Accuracy of materials</h2>

            <p>
              The materials appearing on Campsite's website could include
              technical, typographical, or photographic errors. Campsite does
              not warrant that any of the materials on its website are accurate,
              complete or current. Campsite may make changes to the materials
              contained on its website at any time without notice. However
              Campsite does not make any commitment to update the materials.
            </p>

            <h2>Termination</h2>

            <p>
              We may terminate or suspend your account and bar access to Service
              immediately, without prior notice or liability, under our sole
              discretion, for any reason whatsoever and without limitation,
              including but not limited to a breach of Terms.
            </p>

            <p>
              If you wish to terminate your account, you may simply discontinue
              using Service.
            </p>

            <p>
              All provisions of Terms which by their nature should survive
              termination shall survive termination, including, without
              limitation, ownership provisions, warranty disclaimers, indemnity,
              and limitations of liability.
            </p>

            <h2>Modifications</h2>

            <p>
              Campsite may revise these terms of service for its website at any
              time without notice. By using this website you are agreeing to be
              bound by the then current version of these terms of service.
            </p>
          </Rte>
        </div>
      </div>
    </div>
  </Basic>
</template>

<script>
import defaultOG from '@/assets/default-og';
import Basic from '@/components/basic';
import Rte from '@/components/rte';
import CopyrightAgent from '@/components/copyright-agent';

export default {
  head: {
    title: 'Terms - Campsite.bio',
    meta: [
      ...defaultOG,
      {
        hid: 'description',
        name: 'description',
        content: 'This is the current terms of service for Campsite',
      },
    ],
  },

  components: { Basic, Rte, CopyrightAgent },
};
</script>

<style lang="scss" scoped></style>
